import { BookingComponent, BookingDepartment, BookingFeature, BookingLocation, BookingPort } from '../../../../services/booking/types';
import { BookingFeatureTypes } from '../../Shared/enums';

export interface CleanExport {
    floorPlanName: string;
    floorPlanId: number;
    locationId: number;
    locationName?: string;
    categoryId: number;
    isImporting: true;
    exportedAt: string;
    deskFeatures: ProjectedDesk[];
    zoneFeatures: ProjectedZone[];
    wallFeatures: ProjectedWall[];
    officeFeatures: ProjectedOffice[];
    labelFeatures: ProjectedLabel[];
}

export interface BuildCleanExportParams {
    floorPlanName: string;
    floorPlanId: number;
    locationId: number;
    categoryId: number;
    floorPlanFeatures: BookingFeature[];
    lookups?: ExportLookups;
}

interface BaseProjection {
    id: number;
    statusId: number;
    label: string;
    x: number;
    y: number;
    width: number;
    height: number;
    fill: string;
    borderRadius: number;
}

type ShapeBaseProjection = Omit<BaseProjection, 'borderRadius'>;

export interface ProjectedDesk extends BaseProjection {
    ports: BookingPort[];
    components?: BookingComponent[];
    rotationAngle?: number;
    assetTag?: string;
    qrCode?: string;
    desc?: { deskDesc?: string };
}

export interface ProjectedZone extends ShapeBaseProjection {
    zoneId?: number;
    name?: string;
    belongsTo?: number;
    belongsToName?: string;
    svg?: unknown[];
}

export interface ProjectedWall extends ShapeBaseProjection {
    startPoint?: { x: number; y: number };
    endPoint?: { x: number; y: number };
    thickness?: number;
}

export type ProjectedOffice = BaseProjection;

export interface ProjectedLabel extends BaseProjection {
    color?: string;
    fontSize?: number;
    chip?: boolean;
}

export interface ExportLookups {
    locations?: BookingLocation[];
    departments?: BookingDepartment[];
}

const findLocationName = (lookups: ExportLookups | undefined, locationId: number | undefined): string | undefined => {
    if (!lookups?.locations || locationId == null) return undefined;
    return lookups.locations.find((l) => l.id === locationId)?.name;
};

const findDepartmentName = (lookups: ExportLookups | undefined, departmentId: number | undefined): string | undefined => {
    if (!lookups?.departments || departmentId == null) return undefined;
    return lookups.departments.find((d) => d.id === departmentId)?.name;
};

const pickBase = (f: BookingFeature): BaseProjection => ({
    id: f.id,
    statusId: f.statusId,
    label: f.label,
    x: f.x,
    y: f.y,
    width: f.width,
    height: f.height,
    fill: f.fill,
    borderRadius: f.borderRadius,
});

const pickShapeBase = (f: BookingFeature): ShapeBaseProjection => {
    const { borderRadius, ...rest } = pickBase(f);
    return rest;
};

/* assetTag/qrCode/desc are not lifted from additionalInfo by processFeatures,
   so the desk projector parses them here. Other types already have their
   lifted fields at top-level after processFeatures runs on fetch. */
const parseDeskAdditionalInfo = (
    raw: string | undefined | null,
): { assetTag?: string; qrCode?: string; desc?: { deskDesc?: string } } => {
    if (!raw || !raw.trim()) return {};
    try {
        const info = JSON.parse(raw);
        const result: { assetTag?: string; qrCode?: string; desc?: { deskDesc?: string } } = {};
        if (info.assetTag != null) result.assetTag = info.assetTag;
        if (info.qrCode != null) result.qrCode = info.qrCode;
        if (info.desc != null) result.desc = info.desc;
        return result;
    } catch {
        return {};
    }
};

export const projectDesk = (f: BookingFeature): ProjectedDesk => {
    const lifted = parseDeskAdditionalInfo(f.additionalInfo);
    const desk = f as BookingFeature & {
        rotationAngle?: number;
        assetTag?: string;
        qrCode?: string;
        desc?: { deskDesc?: string };
    };
    return {
        ...pickBase(f),
        ports: f.ports ?? [],
        components: f.components,
        rotationAngle: desk.rotationAngle,
        assetTag: lifted.assetTag ?? desk.assetTag,
        qrCode: lifted.qrCode ?? desk.qrCode,
        desc: lifted.desc ?? desk.desc,
    };
};

export const projectZone = (f: BookingFeature, lookups?: ExportLookups): ProjectedZone => {
    const zone = f as BookingFeature & {
        zoneId?: number;
        name?: string;
        belongsTo?: number;
        svg?: unknown[];
    };
    return {
        ...pickShapeBase(f),
        zoneId: zone.zoneId ?? f.id,
        name: zone.name,
        belongsTo: zone.belongsTo,
        belongsToName: findDepartmentName(lookups, zone.belongsTo),
        svg: zone.svg,
    };
};

export const projectWall = (f: BookingFeature): ProjectedWall => {
    const wall = f as BookingFeature & {
        startPoint?: { x: number; y: number };
        endPoint?: { x: number; y: number };
        thickness?: number;
    };
    return {
        ...pickShapeBase(f),
        startPoint: wall.startPoint,
        endPoint: wall.endPoint,
        thickness: wall.thickness,
    };
};

export const projectOffice = (f: BookingFeature): ProjectedOffice => pickBase(f);

export const projectLabel = (f: BookingFeature): ProjectedLabel => {
    const label = f as BookingFeature & {
        color?: string;
        fontSize?: number;
        chip?: boolean;
    };
    return {
        ...pickBase(f),
        color: label.color,
        fontSize: label.fontSize,
        chip: label.chip,
    };
};

export const buildCleanExport = ({
    floorPlanName,
    floorPlanId,
    locationId,
    categoryId,
    floorPlanFeatures,
    lookups,
}: BuildCleanExportParams): CleanExport => {
    const deskFeatures: ProjectedDesk[] = [];
    const zoneFeatures: ProjectedZone[] = [];
    const wallFeatures: ProjectedWall[] = [];
    const officeFeatures: ProjectedOffice[] = [];
    const labelFeatures: ProjectedLabel[] = [];

    floorPlanFeatures.forEach((f) => {
        switch (f.typeId) {
            case BookingFeatureTypes.Desk:
                deskFeatures.push(projectDesk(f));
                break;
            case BookingFeatureTypes.Zone:
                zoneFeatures.push(projectZone(f, lookups));
                break;
            case BookingFeatureTypes.Wall:
                wallFeatures.push(projectWall(f));
                break;
            case BookingFeatureTypes.OfficeOrMeeting:
                officeFeatures.push(projectOffice(f));
                break;
            case BookingFeatureTypes.Label:
                labelFeatures.push(projectLabel(f));
                break;
            default:
                break;
        }
    });

    return {
        floorPlanName,
        floorPlanId,
        locationId,
        locationName: findLocationName(lookups, locationId),
        categoryId,
        isImporting: true,
        exportedAt: new Date().toISOString(),
        deskFeatures,
        zoneFeatures,
        wallFeatures,
        officeFeatures,
        labelFeatures,
    };
};

/* ===== Import-side helpers ===== */

/**
 * The legacy pre-cleanup export format had a flat `floorPlanFeatures` array.
 * The current format splits features by type (`deskFeatures`, `zoneFeatures`, etc).
 */
export const isLegacyExportFormat = (data: Record<string, unknown>): boolean =>
    !Array.isArray(data.deskFeatures) || data.floorPlanFeatures !== undefined;

export type ResolveImportLocationIdResult =
    | { ok: true; locationId: number }
    | { ok: false; error: string };

/**
 * Resolves the target-env LocationId for an imported floor plan.
 * If `data.locationName` is present, looks it up in the target env's locations
 * cache and fails loud if it doesn't resolve. Otherwise falls back to
 * `data.locationId` as-is (backwards-compat with legacy exports).
 */
export const resolveImportLocationId = (
    data: { locationId: number; locationName?: string },
    locations: BookingLocation[],
): ResolveImportLocationIdResult => {
    if (typeof data.locationName === 'string' && data.locationName.trim() !== '') {
        const matched = locations.find(
            (l) => l.name.toLowerCase() === (data.locationName as string).toLowerCase(),
        );
        if (!matched) {
            return { ok: false, error: `Location "${data.locationName}" was not found in this environment.` };
        }
        return { ok: true, locationId: matched.id };
    }
    return { ok: true, locationId: data.locationId };
};

/* The slim export shape drops the additionalInfo blob, but the Designer's
   edit dialogs and renderers still read fields out of it. Hydration has to
   serialise the top-level export fields back into the per-type blob that
   the API persists on a fresh CREATE, otherwise opening the Edit Desk
   dialog reads an empty blob and overwrites the top-level fields with
   blanks on save, and the zone renderer has no svg polygon to draw. */
const buildAdditionalInfo = (
    feature: Record<string, unknown>,
    typeId: BookingFeatureTypes,
): string => {
    const featureId = (feature.id as number) ?? 0;
    switch (typeId) {
        case BookingFeatureTypes.Desk:
            return JSON.stringify({
                assetTag: feature.assetTag ?? '',
                qrCode: feature.qrCode ?? '',
                desc: feature.desc ?? { deskDesc: '' },
                rotationAngle: feature.rotationAngle ?? 0,
                FeatureId: featureId,
            });
        case BookingFeatureTypes.Zone:
            return JSON.stringify({
                svg: feature.svg ?? [],
                colors: { badge: '#20C5A0' },
                name: feature.name ?? '',
                belongsTo: feature.belongsTo ?? 0,
                featureId: 0,
            });
        case BookingFeatureTypes.Wall:
            return JSON.stringify({
                startPoint: feature.startPoint ?? { x: 0, y: 0 },
                endPoint: feature.endPoint ?? { x: 0, y: 0 },
                thickness: feature.thickness ?? 5,
                FeatureId: featureId,
            });
        case BookingFeatureTypes.OfficeOrMeeting:
            return JSON.stringify({ FeatureId: featureId });
        case BookingFeatureTypes.Label:
            return JSON.stringify({
                color: feature.color ?? '#000',
                fontSize: feature.fontSize ?? 12,
                chip: feature.chip ?? false,
            });
        default:
            return '';
    }
};

/**
 * Hydrates the per-type feature arrays from an imported export into a single
 * flat `BookingFeature[]` that the Designer's Redux state expects, stamping
 * the resolved floor-plan / category / location IDs onto every feature and
 * rebuilding the per-type additionalInfo blob from the slim export's
 * top-level fields. `status` is intentionally dropped — the API's
 * FeatureStatus enum can't deserialise an empty string and uses `statusId`.
 */
export const hydrateImportedFeatures = (
    data: Record<string, unknown>,
    fpId: number,
    catId: number,
    locId: number,
): BookingFeature[] => {
    const hydrate = (
        list: Record<string, unknown>[] | undefined,
        typeId: BookingFeatureTypes,
        typeName: string,
    ): BookingFeature[] => (list ?? []).map((feature) => {
        const { status: _status, ...rest } = feature;
        return {
            ...rest,
            typeId,
            type: typeName,
            floorPlanId: fpId,
            floorPlanCategoryId: catId,
            locationId: locId,
            createdById: 0,
            additionalInfo: buildAdditionalInfo(feature, typeId),
            ports: (feature.ports as unknown[]) ?? [],
            components: (feature.components as unknown[]) ?? [],
            isImported: true,
        } as unknown as BookingFeature;
    });

    return ([] as BookingFeature[]).concat(
        hydrate(data.deskFeatures as Record<string, unknown>[] | undefined, BookingFeatureTypes.Desk, 'Desk'),
        hydrate(data.zoneFeatures as Record<string, unknown>[] | undefined, BookingFeatureTypes.Zone, 'Zone'),
        hydrate(data.wallFeatures as Record<string, unknown>[] | undefined, BookingFeatureTypes.Wall, 'Wall'),
        hydrate(data.officeFeatures as Record<string, unknown>[] | undefined, BookingFeatureTypes.OfficeOrMeeting, 'OfficeOrMeeting'),
        hydrate(data.labelFeatures as Record<string, unknown>[] | undefined, BookingFeatureTypes.Label, 'Label'),
    );
};
